import React, { useState, useEffect } from 'react';
import { FiBell, FiBellOff, FiCheck, FiAlertCircle } from 'react-icons/fi';

// Convert base64 URL-safe VAPID public key to Uint8Array (web-push expects this)
const urlBase64ToUint8Array = (base64String) => {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
};

const NotificationsSettings = ({ token }) => {
  const [permission, setPermission] = useState(
    typeof Notification !== 'undefined' ? Notification.permission : 'default'
  );
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [supported, setSupported] = useState(true);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const apiBase = `${process.env.REACT_APP_BACKEND_URL}/api`;

  useEffect(() => {
    const ok = 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
    setSupported(ok);
    if (ok) refreshSubscriptionStatus();
  }, []);

  const refreshSubscriptionStatus = async () => {
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      setIsSubscribed(!!sub);
    } catch {
      setIsSubscribed(false);
    }
  };

  const enable = async () => {
    setBusy(true); setMsg('');
    try {
      // 1) Permission
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== 'granted') {
        setMsg('Permission denied. Enable notifications in browser settings.');
        return;
      }

      // 2) Get VAPID key
      const keyRes = await fetch(`${apiBase}/push/vapid-key`);
      const { publicKey } = await keyRes.json();
      if (!publicKey) { setMsg('Push not configured on server.'); return; }

      // 3) Subscribe to push manager
      const reg = await navigator.serviceWorker.ready;
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(publicKey),
        });
      }

      // 4) Send subscription to backend
      const subData = sub.toJSON();
      const res = await fetch(`${apiBase}/push/subscribe`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          endpoint: subData.endpoint,
          keys: subData.keys,
          userAgent: navigator.userAgent,
        }),
      });
      if (res.ok) {
        setIsSubscribed(true);
        setMsg('Notifications enabled! 🎉');
      } else {
        setMsg('Failed to save subscription on server.');
      }
    } catch (e) {
      console.error(e);
      setMsg('Failed: ' + (e.message || 'unknown error'));
    } finally {
      setBusy(false);
    }
  };

  const disable = async () => {
    setBusy(true); setMsg('');
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        const endpoint = sub.endpoint;
        await sub.unsubscribe();
        await fetch(`${apiBase}/push/unsubscribe?endpoint=${encodeURIComponent(endpoint)}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` },
        });
      }
      setIsSubscribed(false);
      setMsg('Notifications disabled.');
    } catch (e) {
      setMsg('Failed: ' + (e.message || 'unknown error'));
    } finally {
      setBusy(false);
    }
  };

  const sendTest = async () => {
    setBusy(true); setMsg('');
    try {
      const res = await fetch(`${apiBase}/push/test`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const data = await res.json();
      setMsg(data.sent > 0 ? `Test sent to ${data.sent} device(s) 🎉` : 'Could not deliver test (subscription may be expired).');
    } catch (e) {
      setMsg('Test failed: ' + (e.message || 'unknown'));
    } finally {
      setBusy(false);
    }
  };

  if (!supported) {
    return (
      <div className="form-group">
        <div className="notif-card notif-card-warn">
          <FiAlertCircle size={20} />
          <div>
            <strong>Push notifications are not supported on this browser.</strong>
            <div style={{ fontSize: 13, marginTop: 4 }}>
              On iOS: add this app to your Home Screen first (Safari → Share → Add to Home Screen), then open it from the icon to enable notifications.
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="form-group">
        <label className="form-label">Push Notifications</label>
        <p className="form-helper" style={{ marginBottom: 12 }}>
          Get reminders on this device for tasks starting today, due today, and overdue tasks.
          You'll receive a daily digest at 08:00 (Europe/Istanbul).
        </p>
      </div>

      <div className={`notif-card ${isSubscribed ? 'notif-card-on' : 'notif-card-off'}`}>
        {isSubscribed ? <FiBell size={22} /> : <FiBellOff size={22} />}
        <div style={{ flex: 1 }}>
          <strong>{isSubscribed ? 'Notifications enabled on this device' : 'Notifications disabled'}</strong>
          <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
            Permission: <code>{permission}</code>
          </div>
        </div>
        {isSubscribed ? (
          <button className="btn btn-secondary btn-sm" onClick={disable} disabled={busy} data-testid="disable-push-btn">
            Turn Off
          </button>
        ) : (
          <button className="btn btn-primary btn-sm" onClick={enable} disabled={busy} data-testid="enable-push-btn">
            <FiBell size={14} /> Enable
          </button>
        )}
      </div>

      {isSubscribed && (
        <div className="form-group" style={{ marginTop: 16 }}>
          <button className="btn btn-secondary btn-sm" onClick={sendTest} disabled={busy} data-testid="test-push-btn">
            <FiCheck size={14} /> Send Test Notification
          </button>
        </div>
      )}

      {msg && (
        <div className="form-helper" style={{ marginTop: 12, color: 'var(--monday-blue)' }} data-testid="push-message">
          {msg}
        </div>
      )}
    </div>
  );
};

export default NotificationsSettings;
